import React from "react";
import { Link } from "react-router-dom";
import {
  FaPhoneAlt,
  FaWhatsapp,
  FaMapMarkerAlt,
  FaEnvelope,
  FaArrowRight,
  FaCheckCircle,
} from "react-icons/fa";
import { SITE_CONTENT } from "../content";
import nawabiHeroFeast from "../assets/nawabi_hero_feast.png";
import indianWeddingFeast from "../assets/indian_wedding_feast.png";
import indianBiryaniSpread from "../assets/indian_biryani_spread.png";

const VisitSection = () => {
  const visit = SITE_CONTENT.pages?.homePage?.visitSection || {};
  const title = visit.title || "Ready for a Feast?";
  const phone = visit.phone || "+91 91502 99336";
  const email = visit.email || "nawabicaterers.support@gmail.com";
  const button = visit.button || { label: "Request a Catering Quote", to: "/contact" };

  const mainImage = visit.images?.left || nawabiHeroFeast || indianWeddingFeast;
  const secondaryImage = visit.images?.rightBottom || indianBiryaniSpread;

  return (
    <section className="bg-[#f8f5ee] py-16 sm:py-20 lg:py-24 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Luxury Royal Emerald Card Container */}
        <div className="relative rounded-3xl bg-gradient-to-br from-[#071d10] via-[#092212] to-[#040e07] border border-[#D4AF37]/30 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.4)] overflow-hidden">
          
          {/* Decorative ambient radial glows */}
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-[100px] pointer-events-none" />
          <div className="absolute bottom-0 left-10 w-80 h-80 bg-[#1b4329]/30 rounded-full blur-[90px] pointer-events-none" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center p-8 sm:p-12 lg:p-16 relative z-10">
            
            {/* ── LEFT CONTENT: 7 COLS ── */}
            <div className="lg:col-span-7 flex flex-col justify-center space-y-6">
              
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#E8A040]/15 border border-[#E8A040]/30 text-[#FFD580] text-xs font-semibold tracking-wider uppercase w-fit">
                <span className="w-2 h-2 rounded-full bg-[#E8A040] animate-pulse"></span>
                <span>100% Halal Certified · Outdoor Catering</span>
              </div>

              {/* Heading */}
              <div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white font-normal leading-[1.2] mb-3">
                  {title === "Ready for a Feast?" ? (
                    <>
                      Ready to Treat Your Guests to a{" "}
                      <span className="italic text-[#FFD580] font-normal">Royal Feast?</span>
                    </>
                  ) : (
                    title
                  )}
                </h2>
                <div className="w-20 h-1 bg-gradient-to-r from-[#D4AF37] to-[#E8A040] rounded-full mt-3" />
              </div>

              {/* Description */}
              <p className="text-white/80 text-sm sm:text-base leading-relaxed max-w-xl font-light">
                Salem headquarters, catering across all of Tamil Nadu — Chennai, Coimbatore, Madurai, Erode and beyond. Tell us your date, your headcount, and your favourite non-veg delicacies, and our master chefs will take care of the rest.
              </p>

              {/* Highlights Pill Badges */}
              <div className="flex flex-wrap gap-2 pt-1">
                {[
                  "Freshly Cooked On-Site",
                  "Live Food Counters",
                  "Trained Uniformed Staff",
                  "FSSAI Certified Hygiene",
                ].map((item, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white/[0.05] border border-white/10 text-white/75 text-xs font-medium"
                  >
                    <FaCheckCircle className="text-[#E8A040] text-[11px]" />
                    {item}
                  </span>
                ))}
              </div>

              {/* Contact & Location Info Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-2">
                
                {/* Location */}
                <div className="flex items-center gap-3.5 p-3.5 rounded-2xl bg-white/[0.05] border border-white/10 hover:border-[#E8A040]/40 transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-[#E8A040]/20 flex items-center justify-center text-[#FFD580] shrink-0">
                    <FaMapMarkerAlt className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">
                      Base & Service Coverage
                    </div>
                    <div className="text-xs sm:text-sm font-medium text-white">
                      Salem (HQ) · All Tamil Nadu
                    </div>
                  </div>
                </div>

                {/* Direct Phone / WhatsApp */}
                <a
                  href={`tel:${phone.replace(/\s+/g, "")}`}
                  className="flex items-center gap-3.5 p-3.5 rounded-2xl bg-white/[0.05] border border-white/10 hover:border-[#E8A040]/40 hover:bg-white/[0.08] transition-all group"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#E8A040]/20 flex items-center justify-center text-[#FFD580] shrink-0 group-hover:scale-110 transition-transform">
                    <FaPhoneAlt className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">
                      Direct Catering Line
                    </div>
                    <div className="text-xs sm:text-sm font-semibold text-white group-hover:text-[#FFD580] transition-colors">
                      {phone}
                    </div>
                  </div>
                </a>

                {/* Email Support */}
                <a
                  href={`mailto:${email}`}
                  className="flex items-center gap-3.5 p-3.5 rounded-2xl bg-white/[0.05] border border-white/10 hover:border-[#E8A040]/40 hover:bg-white/[0.08] transition-all group sm:col-span-2"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#E8A040]/20 flex items-center justify-center text-[#FFD580] shrink-0 group-hover:scale-110 transition-transform">
                    <FaEnvelope className="w-4 h-4" />
                  </div>
                  <div className="truncate">
                    <div className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">
                      Email Inquiries
                    </div>
                    <div className="text-xs sm:text-sm font-medium text-white group-hover:text-[#FFD580] transition-colors truncate">
                      {email}
                    </div>
                  </div>
                </a>

              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 sm:gap-3.5 pt-3">
                <Link
                  to={button.to || "/contact"}
                  className="inline-flex items-center justify-center gap-2 px-5 sm:px-8 py-2.5 sm:py-3.5 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#E8A040] text-black font-semibold text-[10px] xs:text-[11px] sm:text-xs tracking-normal sm:tracking-wider uppercase shadow-[0_4px_20px_rgba(232,160,64,0.3)] hover:brightness-110 hover:shadow-[0_6px_25px_rgba(232,160,64,0.45)] transition-all transform hover:-translate-y-0.5 text-center cursor-pointer whitespace-nowrap"
                >
                  <span>{button.label || "Request a Catering Quote"}</span>
                  <FaArrowRight className="text-[10px]" />
                </Link>

                <a
                  href="https://wa.me/919150299336?text=Hi%20Nawabi%20Caterers,%20I%20would%20like%20to%20discuss%20catering%20for%20my%20event."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-5 sm:px-7 py-2.5 sm:py-3.5 rounded-full bg-[#1b4329] border border-[#2d6a42] text-white font-medium text-[10px] xs:text-[11px] sm:text-xs tracking-normal sm:tracking-wider uppercase hover:bg-[#225735] transition-all text-center whitespace-nowrap"
                >
                  <FaWhatsapp className="text-[#25D366] text-sm sm:text-base" />
                  <span>Discuss on WhatsApp</span>
                </a>
              </div>

            </div>

            {/* ── RIGHT VISUAL SHOWCASE: 5 COLS ── */}
            <div className="lg:col-span-5 relative mt-6 lg:mt-0">
              
              {/* Main Grand Feast Visual */}
              <div className="relative rounded-2xl overflow-hidden border-2 border-[#D4AF37]/30 shadow-2xl group">
                <img
                  src={mainImage}
                  alt="Nawabi Royal Feast Setup"
                  className="w-full h-[320px] sm:h-[400px] lg:h-[420px] object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#040e07] via-transparent to-transparent opacity-80" />
                
                {/* Overlay Badge at Bottom */}
                <div className="absolute bottom-4 left-4 right-4 p-4 rounded-xl bg-black/60 backdrop-blur-md border border-white/15 text-white">
                  <div className="text-[#FFD580] text-xs font-semibold uppercase tracking-wider mb-0.5">
                    Tamil Nadu Wide Outdoor Catering
                  </div>
                  <div className="text-xs text-white/80">
                    Weddings · Corporate · Receptions · Private Banquets
                  </div>
                </div>
              </div>

              {/* Floating Secondary Food Badge Card */}
              <div className="hidden sm:flex absolute -top-5 -left-5 p-3 rounded-2xl bg-[#092212]/95 backdrop-blur-md border border-[#D4AF37]/40 shadow-xl items-center gap-3">
                <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0 border border-[#D4AF37]/30">
                  <img
                    src={secondaryImage}
                    alt="Authentic Dum Biryani"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="pr-2">
                  <div className="text-[10px] text-[#FFD580] font-semibold uppercase tracking-wider">
                    Signature Specialty
                  </div>
                  <div className="text-xs font-bold text-white">
                    Authentic Dum Biryani
                  </div>
                </div>
              </div>

            </div>

          </div>

        </div>

      </div>
    </section>
  );
};

export default VisitSection;
